import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BADGE_CATALOG } from '../utils/badges';
import { X } from 'lucide-react';

/**
 * BadgeUnlockToast — fixed top-right popover that celebrates a newly
 * earned milestone badge. Auto-dismisses after 6s. Can stack multiple.
 *
 * Props:
 *   badges: array of badge codes that were just unlocked (pass only new ones)
 *   onDismiss: callback to clear the list
 */
export const BadgeUnlockToast = ({ badges = [], onDismiss }) => {
  useEffect(() => {
    if (!badges.length) return;
    const t = setTimeout(() => onDismiss?.(), 6500);
    return () => clearTimeout(t);
  }, [badges, onDismiss]);

  return (
    <div className="fixed top-20 right-6 z-[80] flex flex-col gap-3 pointer-events-none">
      <AnimatePresence>
        {badges.map((code) => {
          const meta = BADGE_CATALOG[code];
          if (!meta) return null;
          return (
            <motion.div
              key={code}
              initial={{ opacity: 0, x: 50, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 50, scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 280, damping: 22 }}
              className="pointer-events-auto bg-card border-2 border-primary rounded-lg shadow-[0_0_40px_rgba(250,204,21,0.45)] p-4 flex items-center gap-4 min-w-[320px] max-w-[380px]"
              data-testid={`badge-unlock-${code}`}
            >
              <motion.img
                src={meta.image}
                alt={meta.name}
                className="w-16 h-16 object-contain drop-shadow-[0_0_12px_rgba(250,204,21,0.7)]"
                initial={{ rotate: -12, scale: 0.6 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-heading text-[10px] uppercase tracking-[0.2em] text-primary mb-1">
                  Badge Unlocked
                </div>
                <div className="font-heading text-lg text-white leading-tight">{meta.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{meta.description}</div>
              </div>
              <button
                onClick={() => onDismiss?.()}
                className="text-muted-foreground hover:text-white self-start"
                aria-label="close"
                data-testid={`badge-unlock-close-${code}`}
              >
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
